import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Star, MapPin, Phone, Share2, MessageCircle, BadgeCheck, UserPlus, UserCheck, Package, Users, Clock, Calendar, Circle } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatLastSeen, isOnline } from "@/lib/presence";

export default function StorePage() {
    const { slug } = useParams();
    const { t, lang } = useLang();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [followBusy, setFollowBusy] = useState(false);
    const [ownerLastSeen, setOwnerLastSeen] = useState(null);

    useEffect(() => {
        api.get(`/stores/by-slug/${slug}`).then((r) => setData(r.data));
    }, [slug]);

    // Poll presence of the store owner
    useEffect(() => {
        if (!data?.store?.owner_id) return;
        const load = () => api.get(`/chat/presence/${data.store.owner_id}`).then((r) => setOwnerLastSeen(r.data.last_seen)).catch(() => {});
        load();
        const iv = setInterval(load, 20000);
        return () => clearInterval(iv);
    }, [data?.store?.owner_id]);

    if (!data) return <Layout><div className="min-h-[60vh] grid place-items-center text-muted-foreground">...</div></Layout>;

    const { store, products, reviews } = data;
    const sellerOnline = isOnline(ownerLastSeen);

    const share = async () => {
        try { await navigator.clipboard.writeText(window.location.href); toast.success("✓"); } catch {}
    };

    const toggleFollow = async () => {
        if (!user) { toast.error(t("loginRequired")); return; }
        setFollowBusy(true);
        try {
            if (store.is_following) {
                const { data: res } = await api.delete(`/stores/${store.store_id}/follow`);
                setData((d) => ({ ...d, store: { ...d.store, is_following: false, stats: { ...d.store.stats, followers_count: res.followers_count } } }));
            } else {
                const { data: res } = await api.post(`/stores/${store.store_id}/follow`);
                setData((d) => ({ ...d, store: { ...d.store, is_following: true, stats: { ...d.store.stats, followers_count: res.followers_count } } }));
            }
        } catch { toast.error("Error"); } finally { setFollowBusy(false); }
    };

    const contactSeller = async () => {
        if (!user) { toast.error(t("loginRequired")); navigate("/login"); return; }
        try {
            const { data: conv } = await api.post("/chat/conversations", { store_id: store.store_id });
            navigate(`/chat?c=${conv.conversation_id}`);
        } catch { toast.error("Error"); }
    };

    return (
        <Layout>
            {/* Cover */}
            <div className="relative h-56 md:h-80 bg-gradient-to-br from-terracotta/30 to-ochre/30 overflow-hidden" data-testid="store-cover">
                {store.cover_url && <img src={fileUrl(store.cover_url)} alt="" className="w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/60" />
            </div>

            <div className="max-w-7xl mx-auto px-4 md:px-8 -mt-20">
                {/* Header card */}
                <div className="bg-white border border-border rounded-3xl p-6 md:p-8 shadow-sm relative" data-testid="store-header">
                    <div className="flex flex-col md:flex-row items-start gap-5">
                        <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-surface border border-border grid place-items-center overflow-hidden flex-shrink-0 -mt-14 md:-mt-16 shadow-sm">
                            {store.logo_url ? (
                                <img src={fileUrl(store.logo_url)} alt={store.name} className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-5xl font-black text-terracotta">{store.name?.[0]}</span>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="text-2xl md:text-3xl font-black text-espresso" data-testid="store-name">{store.name}</h1>
                                {store.verified && (
                                    <span className="inline-flex items-center gap-1 bg-terracotta/10 text-terracotta text-xs font-bold px-2 py-0.5 rounded-full" data-testid="verified-badge">
                                        <BadgeCheck className="w-3.5 h-3.5" />
                                        {t("verifiedSeller")}
                                    </span>
                                )}
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                {store.rating > 0 && (
                                    <span className="flex items-center gap-1">
                                        <Star className="w-3.5 h-3.5 fill-ochre text-ochre" />
                                        <span className="font-bold text-espresso">{store.rating.toFixed(1)}</span>
                                        <span>({store.stats?.reviews_count || 0})</span>
                                    </span>
                                )}
                                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{store.city}</span>
                                <span className="flex items-center gap-1" data-testid="seller-presence">
                                    {sellerOnline ? <Circle className="w-2 h-2 fill-green-500 text-green-500" /> : <Clock className="w-3 h-3" />}
                                    <span>{ownerLastSeen ? formatLastSeen(ownerLastSeen, t) : store.stats?.response_time}</span>
                                </span>
                                {store.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{store.phone}</span>}
                            </div>
                            {store.description && <p className="mt-4 text-sm text-espresso max-w-2xl leading-relaxed">{store.description}</p>}
                        </div>

                        <div className="flex flex-col gap-2 items-stretch md:items-end w-full md:w-auto">
                            <Button
                                onClick={toggleFollow}
                                disabled={followBusy}
                                className={`rounded-full h-10 px-6 font-bold ${store.is_following ? "bg-surface text-espresso hover:bg-border" : "bg-terracotta hover:bg-terracotta/90 text-white"}`}
                                data-testid="follow-btn"
                            >
                                {store.is_following ? <><UserCheck className="w-4 h-4 me-1.5" />{t("following")}</> : <><UserPlus className="w-4 h-4 me-1.5" />{t("follow")}</>}
                            </Button>
                            <div className="flex gap-2">
                                <Button
                                    onClick={contactSeller}
                                    className="inline-flex items-center gap-1.5 bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-4 py-2 text-sm font-bold transition-colors"
                                    data-testid="contact-seller-btn"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    {t("contactSellerChat")}
                                </Button>
                                <button onClick={share} className="inline-flex items-center gap-1.5 border border-border rounded-full px-4 py-2 text-sm font-bold hover:bg-surface transition-colors" data-testid="share-btn">
                                    <Share2 className="w-4 h-4" />
                                    {t("share")}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Stats row */}
                    <div className="mt-6 pt-6 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="store-stats">
                        <Stat icon={<Package />} label={t("productsCount")} value={store.stats?.products_count || 0} />
                        <Stat icon={<Star />} label={t("reviewsCount")} value={store.stats?.reviews_count || 0} />
                        <Stat icon={<Users />} label={t("followers")} value={store.stats?.followers_count || 0} />
                        <Stat icon={<Calendar />} label={t("yearsOnPlatform")} value={store.stats?.years_on_platform || 1} />
                    </div>
                </div>

                {/* Tabs */}
                <div className="mt-10" data-testid="store-tabs">
                    <Tabs defaultValue="products">
                        <TabsList>
                            <TabsTrigger value="products" data-testid="tab-products">{t("allProducts")}</TabsTrigger>
                            <TabsTrigger value="reviews" data-testid="tab-reviews">{t("reviewsTitle")}</TabsTrigger>
                            <TabsTrigger value="about" data-testid="tab-about">{t("aboutSeller")}</TabsTrigger>
                        </TabsList>

                        <TabsContent value="products" className="mt-6">
                            {products.length === 0 ? (
                                <div className="text-center py-10 text-muted-foreground">{t("noResults")}</div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="store-products-grid">
                                    {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="reviews" className="mt-6">
                            {reviews?.length ? (
                                <div className="grid md:grid-cols-2 gap-4">
                                    {reviews.map((r) => (
                                        <div key={r.review_id} className="bg-white rounded-2xl p-5 border border-border">
                                            <div className="flex items-center justify-between">
                                                <div className="font-bold text-espresso">{r.user_name}</div>
                                                <div className="text-ochre">{"★".repeat(r.rating)}</div>
                                            </div>
                                            {r.comment && <p className="mt-2 text-sm text-espresso leading-relaxed">{r.comment}</p>}
                                            {r.images?.length > 0 && (
                                                <div className="mt-3 flex gap-2 overflow-x-auto">
                                                    {r.images.map((img, i) => (
                                                        <img key={i} src={fileUrl(img)} alt="" className="w-16 h-16 rounded-lg object-cover" />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-10 text-muted-foreground">{t("noResults")}</div>
                            )}
                        </TabsContent>

                        <TabsContent value="about" className="mt-6">
                            <div className="bg-white border border-border rounded-2xl p-6 md:p-8 max-w-2xl">
                                <h3 className="font-black text-espresso text-lg">{t("aboutSeller")}</h3>
                                <p className="mt-3 text-sm text-espresso leading-relaxed whitespace-pre-line">
                                    {store.about_seller || store.description || t("noResults")}
                                </p>
                                <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <div className="text-xs text-muted-foreground uppercase tracking-wide">{t("deliveryInfo")}</div>
                                        <div className="mt-1 font-bold text-espresso">
                                            {lang === "ar"
                                                ? "يتم احتساب رسوم الشحن عند إتمام الطلب"
                                                : "Shipping fee is calculated at checkout"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-muted-foreground uppercase tracking-wide">{t("responseTime")}</div>
                                        <div className="mt-1 font-bold text-espresso">{store.stats?.response_time}</div>
                                    </div>
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
        </Layout>
    );
}

function Stat({ icon, label, value }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-surface grid place-items-center text-terracotta">
                {icon}
            </div>
            <div>
                <div className="text-lg font-black text-espresso leading-none">{value}</div>
                <div className="text-xs text-muted-foreground mt-1">{label}</div>
            </div>
        </div>
    );
}
